import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplyTaskTemplateResponse,
  CaseType,
  TaskTemplate,
  TaskTemplateDetailResponse,
  TaskTemplatesListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

export interface TaskTemplateFilters {
  case_type?: CaseType;
  active?: boolean;
}

export function useTaskTemplates(filters: TaskTemplateFilters = {}) {
  const params = new URLSearchParams();
  if (filters.case_type) params.set('case_type', filters.case_type);
  if (filters.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/task-templates?${qs}` : '/api/task-templates';
  return useQuery({
    queryKey: ['task-templates', { ...filters }],
    queryFn: ({ signal }) => api.get<TaskTemplatesListResponse>(url, signal),
  });
}

export function useTaskTemplate(id: number | null) {
  return useQuery({
    queryKey: ['task-templates', { id }],
    queryFn: ({ signal }) =>
      api.get<TaskTemplateDetailResponse>(`/api/task-templates/${id}`, signal),
    enabled: id !== null,
  });
}

export interface CreateTaskTemplateInput {
  name: string;
  description?: string;
  case_type?: CaseType;
  is_active?: boolean;
  items?: { text: string; is_required?: boolean }[];
}

export function useCreateTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskTemplateInput) =>
      api.post<{ task_template: TaskTemplate }>('/api/task-templates', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task-templates'] });
    },
  });
}

export function useDeleteTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/task-templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task-templates'] });
    },
  });
}

export interface ApplyTaskTemplateInput {
  template_id: number;
  project_id: number;
  assignee_user_id?: number;
  due_date?: number;
}

export interface AddTemplateItemInput {
  template_id: number;
  text: string;
  is_required?: boolean;
}

export function useAddTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, ...body }: AddTemplateItemInput) =>
      api.post<{ item: unknown }>(`/api/task-templates/${template_id}/items`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['task-templates'] });
      void qc.invalidateQueries({ queryKey: ['task-templates', { id: vars.template_id }] });
    },
  });
}

export interface UpdateTemplateItemInput {
  template_id: number;
  item_id: number;
  text?: string;
  is_required?: boolean;
}

export function useUpdateTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, item_id, ...body }: UpdateTemplateItemInput) =>
      api.patch<{ item: unknown }>(
        `/api/task-templates/${template_id}/items/${item_id}`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['task-templates', { id: vars.template_id }] });
    },
  });
}

export function useDeleteTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, item_id }: { template_id: number; item_id: number }) =>
      api.delete<void>(`/api/task-templates/${template_id}/items/${item_id}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['task-templates'] });
      void qc.invalidateQueries({ queryKey: ['task-templates', { id: vars.template_id }] });
    },
  });
}

export function useApplyTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, ...body }: ApplyTaskTemplateInput) =>
      api.post<ApplyTaskTemplateResponse>(
        `/api/task-templates/${template_id}/apply`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['project-tasks', { project_id: vars.project_id }],
      });
    },
  });
}
